import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import { useAdminStore } from '../../../../store/adminStore';
import { formatDateTime } from '../../../../utility/dateFormatter';
import {
  listProspectGlobalCommsSettings,
  updateProspectGlobalCommsSettings,
  listProspectCategoryCommsSettings,
  createProspectCategoryCommsSettings,
  updateProspectCategoryCommsSettings,
  deleteProspectCategoryCommsSettings,
  getProspectGlobalCommsSettingsHistory,
  getProspectCategoryCommsSettingsHistory,
} from '../../../../services/AdminApis';

const CHANNEL = 'EMAIL';

// Mirrors FIELD_BOUNDS in the backend resolver. Values outside these ranges are ignored
// at read time, so we block them here rather than let an admin save a dead setting.
const FIELDS = [
  {
    key: 'signup_free_responses',
    label: 'Free responses on signup',
    min: 0,
    max: 50,
    hint: 'Granted when a prospect in this category converts to a seller.',
  },
  { key: 'prospecting_radius_km', label: 'Prospecting radius (km)', min: 1, max: 5000 },
  { key: 'batch_size', label: 'Batch size', min: 1, max: 1000 },
  { key: 'max_daily_comms', label: 'Max emails / day', min: 0, max: 100 },
  { key: 'max_comms_count', label: 'Max emails total', min: 0, max: 10000 },
  { key: 'cooldown_days', label: 'Cooldown (days)', min: 0, max: 3650 },
];

const errorText = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.message || err?.message || fallback;

const emptyOverride = () =>
  FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {});

/** '' means "inherit" (null on the wire); anything else must be a whole number in range. */
const parseFieldValue = (field, raw) => {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return { ok: true, value: null };

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return { ok: false, message: `${field.label} must be a whole number.` };
  }
  if (parsed < field.min || parsed > field.max) {
    return { ok: false, message: `${field.label} must be between ${field.min} and ${field.max}.` };
  }
  return { ok: true, value: parsed };
};

/** Builds a request body from form state, or returns the first validation error. */
const buildPayload = (formState, { requireAll }) => {
  const payload = {};
  for (const field of FIELDS) {
    const result = parseFieldValue(field, formState[field.key]);
    if (!result.ok) return { error: result.message };
    if (requireAll && result.value === null) {
      return { error: `${field.label} is required for the global default.` };
    }
    payload[field.key] = result.value;
  }
  return { payload };
};

export default function ProspectSignupSettings() {
  const { toast } = useToast();
  const locale = useAdminStore((state) => state.locale);

  const [globalForm, setGlobalForm] = useState(emptyOverride());
  const [globalExists, setGlobalExists] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);

  const [newCategory, setNewCategory] = useState(null);
  const [newOverride, setNewOverride] = useState(emptyOverride());
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyOverride());
  const [savingRowId, setSavingRowId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTitle, setHistoryTitle] = useState('');
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [globals, categoryRows] = await Promise.all([
        listProspectGlobalCommsSettings(),
        listProspectCategoryCommsSettings({ channel: CHANNEL, locale: locale || undefined }),
      ]);

      const emailGlobal = (globals || []).find((row) => row.channel === CHANNEL);
      setGlobalExists(Boolean(emailGlobal));
      setGlobalForm(
        FIELDS.reduce(
          (acc, field) => ({
            ...acc,
            [field.key]: emailGlobal?.[field.key] ?? '',
          }),
          {}
        )
      );
      setOverrides(categoryRows || []);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Unable to load settings',
        description: errorText(err, 'Failed to load prospect signup settings.'),
      });
    } finally {
      setLoading(false);
    }
  }, [locale, toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveGlobal = async () => {
    const { payload, error } = buildPayload(globalForm, { requireAll: true });
    if (error) {
      toast({ variant: 'destructive', title: 'Check the values', description: error });
      return;
    }

    setSavingGlobal(true);
    try {
      await updateProspectGlobalCommsSettings(CHANNEL, payload);
      toast({
        title: 'Global defaults saved',
        description: `Categories without an override now grant ${payload.signup_free_responses} free response(s).`,
      });
      await loadSettings();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Not saved',
        description: errorText(err, 'Failed to save the global defaults.'),
      });
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleCreateOverride = async () => {
    if (!newCategory) {
      toast({
        variant: 'destructive',
        title: 'Pick a category',
        description: 'Search for and select the category this override applies to.',
      });
      return;
    }

    const { payload, error } = buildPayload(newOverride, { requireAll: false });
    if (error) {
      toast({ variant: 'destructive', title: 'Check the values', description: error });
      return;
    }
    if (Object.values(payload).every((value) => value === null)) {
      toast({
        variant: 'destructive',
        title: 'Nothing to override',
        description: 'Set at least one value, otherwise this row would inherit everything.',
      });
      return;
    }

    setCreating(true);
    try {
      await createProspectCategoryCommsSettings({
        category_id: newCategory.id,
        channel: CHANNEL,
        ...payload,
      });
      toast({
        title: 'Override added',
        description: `${newCategory.name} now has its own prospect settings.`,
      });
      setNewCategory(null);
      setNewOverride(emptyOverride());
      await loadSettings();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Override not added',
        description: errorText(err, 'Failed to create the category override.'),
      });
    } finally {
      setCreating(false);
    }
  };

  const startEditing = (row) => {
    setEditingId(row.id);
    setEditDraft(
      FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: row[field.key] ?? '' }), {})
    );
  };

  const handleSaveRow = async (row) => {
    const { payload, error } = buildPayload(editDraft, { requireAll: false });
    if (error) {
      toast({ variant: 'destructive', title: 'Check the values', description: error });
      return;
    }

    setSavingRowId(row.id);
    try {
      await updateProspectCategoryCommsSettings(row.id, payload);
      toast({
        title: 'Override updated',
        description: `${row.category_name || `Category ${row.category_id}`} saved.`,
      });
      setEditingId(null);
      await loadSettings();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Not saved',
        description: errorText(err, 'Failed to update the override.'),
      });
    } finally {
      setSavingRowId(null);
    }
  };

  const handleDelete = async (row) => {
    const name = row.category_name || `Category ${row.category_id}`;
    if (
      !window.confirm(
        `Remove the override for ${name}? It will fall back to its parent category, then the global default.`
      )
    ) {
      return;
    }

    setDeletingId(row.id);
    try {
      await deleteProspectCategoryCommsSettings(row.id);
      toast({ title: 'Override removed', description: `${name} now inherits its settings.` });
      await loadSettings();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Unable to remove',
        description: errorText(err, 'Failed to delete the override.'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const openHistory = async (title, fetcher) => {
    setHistoryTitle(title);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const rows = await fetcher();
      setHistoryRows(rows || []);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Unable to load history',
        description: errorText(err, 'Failed to load the settings history.'),
      });
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const showGlobalHistory = () =>
    openHistory(`Global defaults (${CHANNEL}) — history`, () =>
      getProspectGlobalCommsSettingsHistory(CHANNEL)
    );

  const showCategoryHistory = (row) =>
    openHistory(
      `${row.category_name || `Category ${row.category_id}`} — history`,
      () => getProspectCategoryCommsSettingsHistory(row.category_id, CHANNEL)
    );

  // CategorySearch resolves categories per locale, so without one we cannot offer the picker.
  if (!locale) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold text-gray-800">Signup Free Responses</h1>
        <p className="text-sm text-gray-500 mt-2">
          Select a locale from the header to manage per-category settings. Categories are
          locale-specific, so the overrides below are too.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Signup Free Responses</h1>
          <p className="text-sm text-gray-500 mt-1">
            How many free lead responses a prospect gets when they convert to a seller, plus the
            prospect emailing limits. Values resolve most-specific-first: this category, then its
            parent category, then the global default.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={loadSettings} disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Global defaults ({CHANNEL})</CardTitle>
        </CardHeader>
        <CardContent>
          {!globalExists && !loading && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              No global settings row exists yet. Prospect emailing and signup grants are running on
              the backend's built-in defaults until you save one here.
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-3">
            {FIELDS.map((field) => (
              <div key={field.key}>
                <Label htmlFor={`global-${field.key}`}>{field.label}</Label>
                <Input
                  id={`global-${field.key}`}
                  type="number"
                  min={field.min}
                  max={field.max}
                  value={globalForm[field.key]}
                  onChange={(event) =>
                    setGlobalForm((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  className="mt-2"
                />
                {field.hint && <p className="text-xs text-gray-500 mt-1">{field.hint}</p>}
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={showGlobalHistory}>
              <History size={16} className="mr-2" />
              History
            </Button>
            <Button
              type="button"
              onClick={handleSaveGlobal}
              disabled={savingGlobal || loading}
              className="bg-purple-700 hover:bg-purple-800 text-white"
            >
              {savingGlobal ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Save global defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Add a category override</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Label>Category</Label>
            <div className="mt-2">
              <CategorySearch
                locale={locale}
                onSelect={setNewCategory}
                placeholder="Search category..."
              />
            </div>
            {newCategory ? (
              <div className="mt-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-800">
                  {newCategory.name}
                  {newCategory.type ? ` · ${newCategory.type}` : ''}
                </Badge>
                <Button type="button" variant="ghost" size="sm" onClick={() => setNewCategory(null)}>
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                A parent category applies to every lead beneath it; a child category applies only to
                its own leads and beats its parent.
              </p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {FIELDS.map((field) => (
              <div key={field.key}>
                <Label htmlFor={`new-${field.key}`}>{field.label}</Label>
                <Input
                  id={`new-${field.key}`}
                  type="number"
                  min={field.min}
                  max={field.max}
                  placeholder={
                    globalForm[field.key] === '' ? 'inherit' : `inherit (${globalForm[field.key]})`
                  }
                  value={newOverride[field.key]}
                  onChange={(event) =>
                    setNewOverride((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  className="mt-2"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Leave a field blank to inherit it. Each field inherits independently, so setting only
            the free responses here is safe.
          </p>

          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              onClick={handleCreateOverride}
              disabled={creating || loading}
              className="bg-purple-700 hover:bg-purple-800 text-white"
            >
              {creating ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Plus size={16} className="mr-2" />
              )}
              Add override
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Category overrides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  {FIELDS.map((field) => (
                    <TableHead key={field.key}>{field.label}</TableHead>
                  ))}
                  <TableHead>Effective free responses</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={FIELDS.length + 4} className="text-center text-sm text-gray-500 py-8">
                      {loading
                        ? 'Loading settings...'
                        : 'No overrides yet — every category uses the global defaults.'}
                    </TableCell>
                  </TableRow>
                )}
                {overrides.map((row) => {
                  const isEditing = editingId === row.id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.category_name || `Category ${row.category_id}`}
                        {!row.category_name && (
                          <span className="block text-xs text-red-600">
                            Category missing — this row can never match.
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-gray-100 text-gray-700">{row.category_type || '-'}</Badge>
                      </TableCell>
                      {FIELDS.map((field) => (
                        <TableCell key={field.key}>
                          {isEditing ? (
                            <Input
                              type="number"
                              min={field.min}
                              max={field.max}
                              placeholder="inherit"
                              value={editDraft[field.key]}
                              onChange={(event) =>
                                setEditDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                              }
                              className="w-24"
                            />
                          ) : row[field.key] === null || row[field.key] === undefined ? (
                            <span className="text-gray-400 text-xs">inherit</span>
                          ) : (
                            row[field.key]
                          )}
                        </TableCell>
                      ))}
                      <TableCell>
                        {row.effective_free_responses}
                        <span className="block text-xs text-gray-500">
                          via {row.effective_free_responses_source}
                        </span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleSaveRow(row)}
                              disabled={savingRowId === row.id}
                              className="bg-purple-700 hover:bg-purple-800 text-white mr-2"
                            >
                              {savingRowId === row.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Save size={14} />
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingId(null)}
                              disabled={savingRowId === row.id}
                            >
                              <X size={14} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => showCategoryHistory(row)}
                              className="mr-2"
                              title="View history"
                            >
                              <History size={14} />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(row)}
                              className="mr-2"
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(row)}
                              disabled={deletingId === row.id}
                              className="text-red-600 hover:text-red-700"
                            >
                              {deletingId === row.id ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{historyTitle}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto rounded-md border max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Created at</TableHead>
                  <TableHead>Changed by</TableHead>
                  <TableHead>Status</TableHead>
                  {FIELDS.map((field) => (
                    <TableHead key={field.key}>{field.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={FIELDS.length + 3} className="text-center text-sm text-gray-500 py-8">
                      {historyLoading ? 'Loading history...' : 'No history yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {historyRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.created_at)}</TableCell>
                    <TableCell>{row.admin_email || 'system'}</TableCell>
                    <TableCell>
                      {row.is_active ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Replaced</Badge>
                      )}
                    </TableCell>
                    {FIELDS.map((field) => (
                      <TableCell key={field.key}>
                        {row[field.key] === null || row[field.key] === undefined ? (
                          <span className="text-gray-400 text-xs">inherit</span>
                        ) : (
                          row[field.key]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
